import { useMemo, useState } from "react";
import type { Mall, PlBlock, PlProductRow, PlProductsResponse } from "../../../types/pl";
import { BasisBadge } from "../../../components/BasisBadge";
import { CompBar, type CompItem } from "../../../components/CompBar";
import { num, pct, profitColor, roasFmt, yen } from "../../../lib/plFormat";

const MALLS: Mall[] = ["shopify", "rakuten", "amazon"];
const MALL_LABEL: Record<Mall, string> = { shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon" };
const MALL_COLOR: Record<Mall, string> = { shopify: "#5a8f4e", rakuten: "#c43d3a", amazon: "#e0922a" };

const COMP_DEFS = [
  { key: "cogs", label: "商品原価", color: "var(--pl-cost-cogs)" },
  { key: "fees", label: "手数料", color: "var(--pl-cost-fee)" },
  { key: "ship", label: "送料・物流", color: "var(--pl-cost-ship)" },
  { key: "pts", label: "ポイント・クーポン", color: "var(--pl-cost-pts)" },
  { key: "ad", label: "広告費", color: "var(--pl-cost-ad)" },
  { key: "profit", label: "貢献利益", color: "var(--pl-cost-profit)" },
];

function compItems(block: PlBlock): CompItem[] {
  const sales = block.sales.value || 1;
  const values: Record<string, number> = {
    cogs: block.cogs.value,
    fees: block.mall_fee.value + block.payment_fee.value,
    ship: block.shipping.value,
    pts: block.points_coupon.value,
    ad: block.ad_spend.value,
    profit: Math.max(0, block.contribution),
  };
  return COMP_DEFS.map((c) => ({
    key: c.key,
    label: c.label,
    color: c.color,
    widthPct: (values[c.key] / sales) * 100,
    pctText: pct((c.key === "profit" ? block.contribution : values[c.key]) / sales),
  }));
}

type SortKey = "name" | "units" | "sales" | "cogs" | "fees" | "shipping" | "points" | "ads" | "contribution" | "margin" | "roas";

const COLS: { key: SortKey; label: string }[] = [
  { key: "name", label: "商品" },
  { key: "units", label: "数量" },
  { key: "sales", label: "売上" },
  { key: "cogs", label: "原価" },
  { key: "fees", label: "手数料" },
  { key: "shipping", label: "送料・物流" },
  { key: "points", label: "ポイント等" },
  { key: "ads", label: "広告費" },
  { key: "contribution", label: "貢献利益" },
  { key: "margin", label: "利益率" },
  { key: "roas", label: "ROAS" },
];

function sortValue(row: PlProductRow, key: SortKey): number | string {
  const t = row.total;
  switch (key) {
    case "name":
      return row.name;
    case "units":
      return t.units;
    case "sales":
      return t.sales.value;
    case "cogs":
      return t.cogs.value;
    case "fees":
      return t.mall_fee.value + t.payment_fee.value;
    case "shipping":
      return t.shipping.value;
    case "points":
      return t.points_coupon.value;
    case "ads":
      return t.ad_spend.value;
    case "contribution":
      return t.contribution;
    case "margin":
      return t.margin ?? -Infinity;
    case "roas":
      return t.roas ?? -Infinity;
  }
}

export function ProductTab({ data }: { data: PlProductsResponse }) {
  const { products, mall, unallocated } = data;
  const [sort, setSort] = useState<SortKey>("sales");
  const [dir, setDir] = useState<-1 | 1>(-1);
  const [open, setOpen] = useState<string | null>(null);

  const sorted = useMemo(() => {
    const copy = [...products];
    copy.sort((a, b) => {
      const av = sortValue(a, sort);
      const bv = sortValue(b, sort);
      if (typeof av === "string" || typeof bv === "string") {
        return dir * String(av).localeCompare(String(bv), "ja");
      }
      return dir * (bv - av) * -1;
    });
    return copy;
  }, [products, sort, dir]);

  const total: PlBlock | null = products.length
    ? products.reduce<PlBlock>(
        (acc, p) => ({
          ...acc,
          units: acc.units + p.total.units,
          orders: acc.orders + p.total.orders,
          sales: { value: acc.sales.value + p.total.sales.value, basis: "mixed" },
          cogs: { value: acc.cogs.value + p.total.cogs.value, basis: "mixed" },
          mall_fee: { value: acc.mall_fee.value + p.total.mall_fee.value, basis: "mixed" },
          payment_fee: { value: acc.payment_fee.value + p.total.payment_fee.value, basis: "mixed" },
          shipping: { value: acc.shipping.value + p.total.shipping.value, basis: "mixed" },
          points_coupon: { value: acc.points_coupon.value + p.total.points_coupon.value, basis: "mixed" },
          ad_spend: { value: acc.ad_spend.value + p.total.ad_spend.value, basis: "mixed" },
          ad_sales: { value: acc.ad_sales.value + p.total.ad_sales.value, basis: "mixed" },
          refunds: { value: acc.refunds.value + p.total.refunds.value, basis: "mixed" },
          contribution: acc.contribution + p.total.contribution,
          gross_profit: acc.gross_profit + p.total.gross_profit,
          margin: null,
          roas: null,
          aov: null,
        }),
        {
          units: 0,
          orders: 0,
          sales: { value: 0, basis: "mixed" },
          refunds: { value: 0, basis: "mixed" },
          cogs: { value: 0, basis: "mixed" },
          mall_fee: { value: 0, basis: "mixed" },
          payment_fee: { value: 0, basis: "mixed" },
          shipping: { value: 0, basis: "mixed" },
          points_coupon: { value: 0, basis: "mixed" },
          ad_spend: { value: 0, basis: "mixed" },
          ad_sales: { value: 0, basis: "mixed" },
          gross_profit: 0,
          contribution: 0,
          margin: null,
          roas: null,
          aov: null,
        }
      )
    : null;
  if (total) {
    total.margin = total.contribution / (total.sales.value || 1);
    total.roas = total.ad_sales.value / (total.ad_spend.value || 1);
  }

  const mallFilterLabel = mall === "all" ? "全モール" : MALL_LABEL[mall];

  return (
    <div className="pl-card">
      <div className="pl-card-head">
        <span className="pl-card-title">
          商品別 損益 <span style={{ fontWeight: 400, color: "var(--pl-muted)", fontSize: 13 }}>{mallFilterLabel}</span>
        </span>
        <span className="pl-card-sub">列名で並び替え ・ 行をクリックでモール別内訳</span>
      </div>

      {products.length === 0 ? (
        <div className="pl-empty">対象期間・モールにデータがありません。</div>
      ) : (
        <div className="pl-table-wrap">
          <div className="pl-table" style={{ minWidth: 1180 }}>
            <div className="pl-table-head-row" style={{ gridTemplateColumns: "minmax(240px,2.4fr) repeat(10,minmax(82px,1fr))" }}>
              {COLS.map((c) => (
                <button
                  key={c.key}
                  className={`pl-prod-head-btn ${sort === c.key ? "active" : ""}`}
                  style={{ textAlign: c.key === "name" ? "left" : "right" }}
                  onClick={() => (sort === c.key ? setDir((d) => (d === -1 ? 1 : -1)) : (setSort(c.key), setDir(-1)))}
                >
                  {c.label}
                  {sort === c.key ? (dir < 0 ? " ↓" : " ↑") : ""}
                </button>
              ))}
            </div>

            {sorted.map((p) => {
              const isOpen = open === p.sku;
              const t = p.total;
              return (
                <div className={`pl-prod-row ${isOpen ? "open" : ""}`} key={p.sku}>
                  <div
                    className="pl-prod-row-main"
                    style={{ gridTemplateColumns: "minmax(240px,2.4fr) repeat(10,minmax(82px,1fr))" }}
                    onClick={() => setOpen(isOpen ? null : p.sku)}
                  >
                    <span className="pl-prod-name-cell">
                      <span className="pl-prod-name">
                        {p.name}
                        {p.cost_missing && <span className="pl-warning"> ・原価未登録</span>}
                      </span>
                      <span className="pl-prod-meta">
                        {p.sku} ・ {yen(p.list_price)}
                      </span>
                    </span>
                    <span style={{ textAlign: "right" }}>{num(t.units)}</span>
                    <span style={{ textAlign: "right", fontWeight: 500 }}>
                      {yen(t.sales.value)} <BasisBadge basis={t.sales.basis} note={t.sales.note} />
                    </span>
                    <span style={{ textAlign: "right" }}>{p.cost_missing ? "—" : yen(t.cogs.value)}</span>
                    <span style={{ textAlign: "right" }}>{yen(t.mall_fee.value + t.payment_fee.value)}</span>
                    <span style={{ textAlign: "right" }}>{yen(t.shipping.value)}</span>
                    <span style={{ textAlign: "right" }}>{yen(t.points_coupon.value)}</span>
                    <span style={{ textAlign: "right" }}>{yen(t.ad_spend.value)}</span>
                    <span style={{ textAlign: "right", fontWeight: 600, color: profitColor(t.contribution) }}>{yen(t.contribution)}</span>
                    <span style={{ textAlign: "right", color: profitColor(t.contribution) }}>{pct(t.margin)}</span>
                    <span style={{ textAlign: "right" }}>{roasFmt(t.roas)}</span>
                  </div>
                  {isOpen && (
                    <div className="pl-prod-detail">
                      <div className="pl-prod-sub-table">
                        <div className="pl-prod-sub-head">
                          <span>モール</span>
                          <span style={{ textAlign: "right" }}>数量</span>
                          <span style={{ textAlign: "right" }}>売上</span>
                          <span style={{ textAlign: "right" }}>広告費</span>
                          <span style={{ textAlign: "right" }}>利益</span>
                          <span style={{ textAlign: "right" }}>利益率</span>
                        </div>
                        {MALLS.filter((m) => p.malls[m]).map((m) => {
                          const mb = p.malls[m]!;
                          return (
                            <div className="pl-prod-sub-row" key={m}>
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: MALL_COLOR[m] }} />
                                {MALL_LABEL[m]}
                              </span>
                              <span style={{ textAlign: "right" }}>{num(mb.units)}</span>
                              <span style={{ textAlign: "right" }}>{yen(mb.sales.value)}</span>
                              <span style={{ textAlign: "right" }}>{yen(mb.ad_spend.value)}</span>
                              <span style={{ textAlign: "right", color: profitColor(mb.contribution) }}>{yen(mb.contribution)}</span>
                              <span style={{ textAlign: "right", color: profitColor(mb.contribution) }}>{pct(mb.margin)}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12 }}>
                        <span style={{ color: "var(--pl-muted)" }}>売上に対する費用構成</span>
                        <CompBar items={compItems(t)} height={22} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {total && (
              <div
                className="pl-table-row pl-row-total"
                style={{ gridTemplateColumns: "minmax(240px,2.4fr) repeat(10,minmax(82px,1fr))", fontWeight: 600, padding: "12px 0" }}
              >
                <span>合計</span>
                <span style={{ textAlign: "right" }}>{num(total.units)}</span>
                <span style={{ textAlign: "right" }}>{yen(total.sales.value)}</span>
                <span style={{ textAlign: "right" }}>{yen(total.cogs.value)}</span>
                <span style={{ textAlign: "right" }}>{yen(total.mall_fee.value + total.payment_fee.value)}</span>
                <span style={{ textAlign: "right" }}>{yen(total.shipping.value)}</span>
                <span style={{ textAlign: "right" }}>{yen(total.points_coupon.value)}</span>
                <span style={{ textAlign: "right" }}>{yen(total.ad_spend.value)}</span>
                <span style={{ textAlign: "right", color: profitColor(total.contribution) }}>{yen(total.contribution)}</span>
                <span style={{ textAlign: "right" }}>{pct(total.margin)}</span>
                <span style={{ textAlign: "right" }}>{roasFmt(total.roas)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {unallocated.ad_spend.value > 0 && (
        <p className="pl-footer-note">
          未按分の広告費: {yen(unallocated.ad_spend.value)}
          <BasisBadge basis={unallocated.ad_spend.basis} note={unallocated.ad_spend.note} />
          （セット売上が0の期間などで商品に按分できなかった分）
        </p>
      )}
    </div>
  );
}
